import { HELP_TR, type HelpContent, type HelpKey } from './entries.tr';
import { HELP_EN } from './entries.en';
import { HELP_DE, MOBILE_HELP_DE } from './entries.de';
import { HELP_AR, MOBILE_HELP_AR } from './entries.ar';
import { MOBILE_HELP_EN, MOBILE_HELP_TR, type MobileHelpKey } from './entries.mobile';

export { HELP_TR, HELP_EN, HELP_DE, HELP_AR, type HelpContent, type HelpKey };

/** Yardım metinlerinin dilleri (F7-17): tr tam; en, de, ar başlık + kısa açıklama, ayrıntı Türkçeye düşer. */
export const HELP_LOCALES = ['tr', 'en', 'de', 'ar'] as const;

/** Panelin yardım anahtarları (HelpTip / PageFrame tipleri bunlarla sınırlıdır). */
export const HELP_KEYS = Object.keys(HELP_TR) as HelpKey[];

/** Android yönetim uygulamasına özgü yardım anahtarları (F10-06; metinler entries.mobile.ts). */
export const MOBILE_HELP_KEYS = Object.keys(MOBILE_HELP_TR) as MobileHelpKey[];

/** Panel + mobil: veritabanı tohumu ve paneldeki yardım düzenleyicisi bu listenin tamamını kullanır. */
export const ALL_HELP_KEYS: string[] = [...HELP_KEYS, ...MOBILE_HELP_KEYS];

export interface HelpSeedRow extends HelpContent {
  key: string;
  locale: string;
}

const PANEL_BY_LOCALE = { en: HELP_EN, de: HELP_DE, ar: HELP_AR } as const;
const MOBILE_BY_LOCALE = { en: MOBILE_HELP_EN, de: MOBILE_HELP_DE, ar: MOBILE_HELP_AR } as const;

/**
 * Veritabanına yazılacak tüm yardım kayıtları: tr tam; en, de, ar (F7-17) başlık/kısa açıklama + tr ayrıntı.
 */
export const HELP_ENTRIES: HelpSeedRow[] = [
  ...HELP_KEYS.map((key) => ({ key, locale: 'tr', ...(HELP_TR[key] as HelpContent) })),
  ...(['en', 'de', 'ar'] as const).flatMap((locale) =>
    HELP_KEYS.map((key) => ({
      key,
      locale,
      ...(HELP_TR[key] as HelpContent),
      ...PANEL_BY_LOCALE[locale][key],
    })),
  ),
  ...MOBILE_HELP_KEYS.map((key) => ({ key, locale: 'tr', ...(MOBILE_HELP_TR[key] as HelpContent) })),
  ...(['en', 'de', 'ar'] as const).flatMap((locale) =>
    MOBILE_HELP_KEYS.map((key) => ({
      key,
      locale,
      ...(MOBILE_HELP_TR[key] as HelpContent),
      ...MOBILE_BY_LOCALE[locale][key],
    })),
  ),
];

/**
 * Dil başına yardım metni kapsamı (F7-17): başlığı ve kısa açıklaması o dilde yazılmış kayıt sayısı (panel + mobil).
 * Ayrıntı alanları (ne işe yarar, ne zaman kullanılır…) tr dışındaki dillerde Türkçe kayda düşer.
 */
export function helpCoverage(): Array<{
  locale: (typeof HELP_LOCALES)[number];
  total: number;
  missing: number;
}> {
  const has = (v: { title?: string; short?: string } | undefined) => !!v?.title?.trim() && !!v?.short?.trim();
  return HELP_LOCALES.map((locale) => {
    if (locale === 'tr') return { locale, total: ALL_HELP_KEYS.length, missing: 0 };
    const panel = PANEL_BY_LOCALE[locale] as Partial<Record<string, { title?: string; short?: string }>>;
    const mobile = MOBILE_BY_LOCALE[locale] as Partial<Record<string, { title?: string; short?: string }>>;
    const missing =
      HELP_KEYS.filter((k) => !has(panel[k])).length + MOBILE_HELP_KEYS.filter((k) => !has(mobile[k])).length;
    return { locale, total: ALL_HELP_KEYS.length, missing };
  });
}

export function isHelpKey(k: string): k is HelpKey {
  return Object.hasOwn(HELP_TR, k);
}
